/**
 * O que faz: Define uma página fixa, com cabeçalho e rodapé, para o relatório exportável.
 * Onde usa: DashboardReportDocument nos formatos A4 e Mobile.
 * Conexões: dashboardReportFormats e conteúdo das seções do relatório.
 * Tabelas: Nenhuma.
 * Data: 27/07/2026.
 */

import type { PropsWithChildren, ReactNode } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DASHBOARD_REPORT_FORMATS, type DashboardReportFormat } from '@/features/dashboard/report/dashboardReportFormats';
import type { DashboardReportModel } from '@/features/dashboard/report/buildDashboardReportModel';

interface DashboardReportPageProps extends PropsWithChildren {
  format: DashboardReportFormat;
  pageNumber: number;
  totalPages: number;
  model: DashboardReportModel;
  eyebrow?: string;
  title: string;
  actions?: ReactNode;
}

export const DashboardReportPage = ({
  format: reportFormat,
  pageNumber,
  totalPages,
  model,
  eyebrow,
  title,
  actions,
  children,
}: DashboardReportPageProps) => {
  const page = DASHBOARD_REPORT_FORMATS[reportFormat];
  const isMobile = reportFormat === 'mobile';
  // Protege a abertura do modal caso uma atualização de estado entregue uma data incompleta por um instante.
  const generatedAt = model.generatedAt instanceof Date && !Number.isNaN(model.generatedAt.getTime())
    ? model.generatedAt
    : new Date();

  return (
    <section
      data-dashboard-report-page
      data-page-number={pageNumber}
      style={{
        width: `${page.pixelWidth}px`,
        height: `${page.pixelHeight}px`,
        fontFamily: 'DM Sans, Arial, sans-serif',
        background: 'linear-gradient(145deg, #FAFCF7 0%, #F3F8EC 54%, #FFFFFF 100%)',
        color: '#173324',
      }}
      className={`box-border flex flex-col overflow-hidden ${isMobile ? 'px-[54px] py-[50px]' : 'px-[92px] py-[78px]'}`}
    >
      <header className="flex items-start justify-between border-b border-[#DDE8D0] pb-7">
        <div className="min-w-0">
          <p className="mb-3 text-[20px] font-bold uppercase tracking-[0.11em] text-[#4E8614]">
            {eyebrow ?? 'Relatório de performance'}
          </p>
          <h1
            className={`${isMobile ? 'text-[52px]' : 'text-[62px]'} leading-[1.04] tracking-[-0.045em] text-[#102E1E]`}
            style={{ fontFamily: 'Sora, Arial, sans-serif', fontWeight: 800 }}
          >
            {title}
          </h1>
          <p className="mt-3 text-[24px] text-[#617164]">
            {model.clinicName} <span className="mx-2 text-[#B8F33B]">•</span> {model.periodLabel}
          </p>
        </div>
        <div className="ml-8 shrink-0 text-right text-[19px] leading-8 text-[#617164]">
          <p className="font-bold text-[#173324]">Gerado em {format(generatedAt, 'dd/MM/yyyy', { locale: ptBR })}</p>
          <p>{format(generatedAt, 'HH:mm', { locale: ptBR })}</p>
          {actions}
        </div>
      </header>

      <div className="min-h-0 flex-1 py-8">{children}</div>

      <footer className="flex items-center justify-between border-t border-[#DDE8D0] pt-5 text-[17px] text-[#6B7B6C]">
        <span>Agenda Cheia • Dados do período selecionado</span>
        <span className="font-semibold text-[#355F1A]">Página {pageNumber} de {totalPages}</span>
      </footer>
    </section>
  );
};
